/**
 * A runtime event for the conversation in progress, pushed live by the voice
 * service. Same events and payloads as the project's event ledger
 * (`/v1/events`): `type` is the short type, e.g. `phone.tool.called`; `time`
 * is epoch ms; `data.seq` orders events within the call.
 */
export interface TalkEvent {
  id: string;
  type: string;
  subject: string;
  time: number;
  data: Record<string, unknown>;
}

/**
 * What was said, as the model transcribes it. The agent's words stream as
 * `delta`s on one `itemId` and end with the full `text`; the caller's line
 * arrives once, complete.
 */
export type TalkTranscript =
  | { role: 'agent'; itemId: string; delta: string }
  | { role: 'agent'; itemId: string; text: string; done: true }
  | { role: 'caller'; itemId: string; text: string; done: true };

export type TalkStatus = 'connecting' | 'connected' | 'ended' | 'error';

/** Loudness of each side, 0 to 1, smoothed for animation. */
export interface TalkLevels {
  agent: number;
  caller: number;
}

export interface TalkOptions {
  /** The agent's LineAI number, E.164. */
  agent: string;
  /**
   * Caller-identity token minted server-side (POST /v1/phone/embed/tokens).
   * Without one the caller is anonymous.
   */
  token?: string;
  /** Called at connect time for a fresh token. Takes precedence over `token`. */
  tokenProvider?: () => Promise<string | null>;
  /** Log connection timing to the console. */
  debug?: boolean;
}

export interface TalkListeners {
  status: (status: TalkStatus) => void;
  event: (event: TalkEvent) => void;
  transcript: (transcript: TalkTranscript) => void;
  error: (message: string) => void;
  mute: (muted: boolean) => void;
}

export interface Talk {
  readonly status: TalkStatus;
  readonly muted: boolean;
  /** Current loudness of the agent and the caller. Cheap; call it every frame. */
  levels(): TalkLevels;
  /** Mute or unmute the caller's microphone. Toggles when called with no argument. Returns the new state. */
  mute(muted?: boolean): boolean;
  /** Hang up. Safe to call at any point, including while connecting. */
  end(): void;
  /** Subscribe. Returns a function that unsubscribes. */
  on<K extends keyof TalkListeners>(name: K, listener: TalkListeners[K]): () => void;
}

/** What a transport reports back to the conversation. Internal. */
export interface TransportHooks {
  status: (status: 'connecting' | 'connected' | 'disconnected' | 'error') => void;
  error: (message: string) => void;
  event: (event: TalkEvent) => void;
  transcript: (transcript: TalkTranscript) => void;
  /** The caller's microphone stream, once granted. */
  callerStream: (stream: MediaStream) => void;
  /** The agent's audio as a stream (WebRTC transport). */
  agentStream: (stream: MediaStream) => void;
  /** Shared audio context, created inside the user's tap so playback is allowed. */
  ctx: AudioContext;
  /** Where decoded agent audio goes (WebSocket transport). Already routed to the speakers and the level meter. */
  agentOut: AudioNode;
  log: (...args: unknown[]) => void;
}

export interface Transport {
  call: (agent: string, token: string | null) => Promise<void>;
  hangUp: () => void;
  setMuted: (muted: boolean) => void;
}

/**
 * The server answered 409: this agent runs on the other voice runtime.
 * Thrown by the first transport so the conversation retries on the other one.
 */
export class RuntimeMismatchError extends Error {
  constructor(readonly runtime: string) {
    super(`agent is on the ${runtime} runtime`);
  }
}
